import {
    ConditionalAccessApplications,
    ConditionalAccessDevicePlatform,
    ConditionalAccessLocations,
} from '@microsoft/microsoft-graph-types-beta';
import {
    ConditionalAccessPolicyForm,
    FormConditions,
    GrantControlsController,
    SessionControlsControllerValues,
} from './cap-generic-form';
import {
    ArrayTypeSchema,
    EnumBaselineProp,
    SchemaPattern,
} from 'projects/angular-clarity/src/app/modules/sway/models/schema';
import {
    ApplicationsSchema,
    AuthenticationFlowSchema,
    BuiltInControlsSchema,
    GrantControlsSchema,
    LocationsFormsSchema,
    GenericCapSchema_Data,
    PlatFormsSchema,
    SessionControlsSchema,
} from 'projects/angular-clarity/src/app/modules/sway/models/baseline-schemas/generic-cap-schema-model';

export function getCAPBaselineDisplayName(schema) {
    return schema?.contains?.properties?.displayName?.const || schema?.not?.contains?.properties?.displayName?.const;
}

const getArrayEnumSchema = <T extends string>(values: T[]): EnumBaselineProp<T> => {
    return {
        type: 'array',
        items: {
            type: 'string',
            enum: values?.length > 0 ? values : undefined, // empty array
        },
        maxItems: values?.length,
        minItems: values?.length,
    };
};

const getPlatforms = (values: {
    includePlatforms: ConditionalAccessDevicePlatform[];
    excludePlatforms: ConditionalAccessDevicePlatform[];
}): PlatFormsSchema => {
    const required = [];

    if (values.includePlatforms.length > 0) {
        required.push('includePlatforms');
        if (values.excludePlatforms.length > 0) {
            required.push('excludePlatforms');
        }
        return {
            type: 'object',
            properties: {
                includePlatforms: getArrayEnumSchema(values.includePlatforms),
                excludePlatforms: getArrayEnumSchema(values.excludePlatforms),
            },
            required,
        };
    } else {
        return {
            type: 'null',
        };
    }
};

const getApplications = (values: ConditionalAccessApplications): ApplicationsSchema => {
    const required = ['includeApplications'];
    return {
        type: 'object',
        properties: {
            includeApplications: getArrayEnumSchema(values.includeApplications),
        },
        required,
    };
};

const getLocations = (values: ConditionalAccessLocations): LocationsFormsSchema => {
    if (values.includeLocations.length > 0) {
        return {
            type: 'object',
            properties: {
                includeLocations: getArrayEnumSchema(values.includeLocations),
                excludeLocations: getArrayEnumSchema(values.excludeLocations),
            },
        };
    } else {
        return {
            type: 'null',
        };
    }
};
const getAuthenticationFlows = (values: string[]): AuthenticationFlowSchema => {
    if (values?.length > 0) {
        return {
            type: 'object',
            properties: {
                transferMethods: getListedStringSchema(values),
            },
            required: ['transferMethods'],
        };
    }

    return {
        type: 'null',
    };
};

const getSessionControl = (session: SessionControlsControllerValues): SessionControlsSchema => {
    if (
        !session.cloudAppSecurity.isEnabled &&
        !session.signInFrequency.isEnabled &&
        !session.continuousAccessEvaluation.mode &&
        !session.disableResilienceDefaults &&
        !session.persistentBrowser.isEnabled
    ) {
        return {
            type: 'null',
        };
    }

    let cloudAppSecurity, signInFrequency, continuousAccessEvaluation, disableResilienceDefaults, persistentBrowser;

    if (session.cloudAppSecurity.isEnabled) {
        cloudAppSecurity = {
            type: 'object',
            properties: {
                cloudAppSecurityType: {
                    type: 'string',
                    const: session.cloudAppSecurity.cloudAppSecurityType,
                },
                isEnabled: {
                    type: 'boolean',
                    const: session.cloudAppSecurity.isEnabled,
                },
            },
        };
    } else {
        cloudAppSecurity = {
            type: 'null',
        };
    }

    if (session.persistentBrowser.isEnabled) {
        persistentBrowser = {
            type: 'object',
            properties: {
                mode: {
                    type: 'string',
                    const: session.persistentBrowser.mode,
                },
                isEnabled: {
                    type: 'boolean',
                    const: session.persistentBrowser.isEnabled,
                },
            },
        };
    } else {
        persistentBrowser = {
            type: 'null',
        };
    }

    if (session.signInFrequency.isEnabled) {
        if (session.signInFrequency.frequencyInterval === 'everyTime') {
            signInFrequency = {
                type: 'object',
                properties: {
                    isEnabled: {
                        type: 'boolean',
                        const: true,
                    },
                    frequencyInterval: {
                        type: 'string',
                        const: session.signInFrequency.frequencyInterval,
                    },
                },
            };
        } else {
            signInFrequency = {
                type: 'object',
                properties: {
                    isEnabled: {
                        type: 'boolean',
                        const: true,
                    },
                    type: {
                        type: 'string',
                        const: session.signInFrequency.type,
                    },
                    value: {
                        type: 'number',
                        const: session.signInFrequency.value,
                    },
                    frequencyInterval: {
                        type: 'string',
                        const: session.signInFrequency.frequencyInterval,
                    },
                    authenticationType: {
                        type: 'string',
                        const: session.signInFrequency.authenticationType,
                    },
                },
            };
        }
    } else {
        signInFrequency = {
            type: 'null',
        };
    }

    if (session.continuousAccessEvaluation.mode) {
        continuousAccessEvaluation = {
            type: 'object',
            properties: {
                mode: {
                    type: 'string',
                    const: session.continuousAccessEvaluation.mode,
                },
            },
        };
    } else {
        continuousAccessEvaluation = {
            type: 'null',
        };
    }

    if (session.disableResilienceDefaults) {
        disableResilienceDefaults = {
            type: 'boolean',
            const: session.disableResilienceDefaults,
        };
    } else {
        disableResilienceDefaults = {
            type: 'null',
        };
    }

    return {
        type: 'object',
        properties: {
            disableResilienceDefaults,
            continuousAccessEvaluation,
            cloudAppSecurity,
            signInFrequency,
            persistentBrowser,
        },
    };
};

const getGrantControls = (grant: GrantControlsController): GrantControlsSchema => {
    if (grant.builtInControls?.length === 0 && !grant.authenticationStrength) {
        return {
            type: 'null',
        };
    }

    let authenticationStrength;
    if (grant.authenticationStrength) {
        authenticationStrength = {
            type: 'object',
            properties: {
                id: {
                    type: 'string',
                    const: grant.authenticationStrength,
                },
            },
        };
    } else {
        authenticationStrength = {
            type: 'null',
        };
    }

    const builtInControls: BuiltInControlsSchema = {
        type: 'array',
        items: {
            type: 'string',
            enum: grant.builtInControls?.length > 0 ? grant.builtInControls : undefined, // empty array
        },
        maxItems: grant.builtInControls?.length,
        minItems: grant.builtInControls?.length,
    };

    return {
        type: 'object',
        properties: {
            builtInControls,
            operator: {
                type: 'string',
                const: grant.operator,
            },
            authenticationStrength,
        },
    };
};

export function createGenericCapPolicySchema(
    form: ConditionalAccessPolicyForm,
): ArrayTypeSchema<GenericCapSchema_Data> {
    const include = {
        config: form.includeUsersConfig,
        roles: form.includeRoles,
        users: form.includeUsers,
        GuestsOrExternalUsers: form.includeGuestsOrExternalUsers,
        groups: form.includeGroups,
    };

    const exclude = {
        roles: form.excludeRoles,
        users: form.excludeUsers,
        groups: form.excludeGroups,
        GuestsOrExternalUsers: form.excludeGuestsOrExternalUsers,
    };

    const settings = {
        ...form.excludeUserSettings,
        ...form.includeUserSettings,
    };
    return {
        type: 'array',
        contains: {
            type: 'object',
            properties: {
                state: {
                    type: 'string',
                    const: form.state,
                },

                displayName: {
                    type: 'string',
                    const: form.displayName,
                },

                conditions: {
                    type: 'object',
                    properties: {
                        users: {
                            type: 'object',
                            properties: {
                                includeRoles: getItems(include.roles, include.config, settings.includeRoles),
                                includeGuestsOrExternalUsers: getIncludeGuestsOrExternalUsers(
                                    include.GuestsOrExternalUsers,
                                    settings.includeGuestsOrExternalUsers,
                                    include.config,
                                ),
                                includeUsers: getIncludeUsersItems(
                                    include.users,
                                    include.config,
                                    settings.includeUsers,
                                ),
                                includeGroups: getItems(include.groups, include.config, settings.includeGroups),

                                excludeRoles: getItems(exclude.roles, 'GuestsOrExternalUsers', settings.excludeRoles),
                                excludeUsers: getItems(exclude.users, 'GuestsOrExternalUsers', settings.excludeUsers),
                                excludeGroups: getItems(
                                    exclude.groups,
                                    'GuestsOrExternalUsers',
                                    settings.excludeGroups,
                                ),

                                excludeGuestsOrExternalUsers: getIncludeGuestsOrExternalUsers(
                                    exclude.GuestsOrExternalUsers,
                                    settings.excludeGuestsOrExternalUsers,
                                    'GuestsOrExternalUsers',
                                ),
                            },
                        },

                        clientAppTypes: getArrayEnumSchema(
                            form.conditions?.clientAppTypes?.length > 0 ? form.conditions?.clientAppTypes : ['all'],
                        ),
                        // set default to all because Microsoft force it to be all

                        platforms: getPlatforms(form.conditions?.platforms),
                        applications: getApplications(form.conditions?.applications),
                        locations: getLocations(form.conditions?.locations),
                        authenticationFlows: getAuthenticationFlows(
                            form.conditions?.authenticationFlows?.transferMethods,
                        ),
                        userRiskLevels: getArrayEnumSchema(form.conditions.userRiskLevels),
                        signInRiskLevels: getArrayEnumSchema(form.conditions.signInRiskLevels),
                        insiderRiskLevels: getListedStringSchema(form.conditions.insiderRiskLevels),
                    },
                    required: getRequiredConditions(form.conditions),
                },

                sessionControls: getSessionControl(form.sessionControls),
                grantControls: getGrantControls(form.grant),
            },

            required: ['state', 'displayName'],
        },
    };
}

function getIncludeUsersItems(
    items: string[],
    includeUsersConfig: 'All' | 'None' | 'GuestsOrExternalUsers',
    isIncluded: boolean,
): EnumBaselineProp {
    if (includeUsersConfig === 'GuestsOrExternalUsers') {
        if (items.length > 0 && isIncluded) {
            return {
                type: 'array',
                items: {
                    enum: [...items],
                    type: 'string',
                },
                minItems: items.length,
                maxItems: items.length,
                uniqueItems: true,
            };
        } else {
            return {
                type: 'array',
                maxItems: 0,
            };
        }
    } else if (includeUsersConfig === 'All' || includeUsersConfig === 'None') {
        return {
            type: 'array',
            maxItems: 1,
            minItems: 1,
            items: {
                type: 'string',
                enum: [includeUsersConfig],
            },
        };
    }
}

function getItems(
    items: string[],
    includeUsersConfig: 'All' | 'None' | 'GuestsOrExternalUsers',
    isIncluded: boolean,
): EnumBaselineProp {
    if (includeUsersConfig === 'All' || includeUsersConfig === 'None' || !isIncluded) {
        return {
            type: 'array',
            maxItems: 0,
        };
    } else if (items.length > 0) {
        return {
            type: 'array',
            items: {
                enum: [...items],
                type: 'string',
            },
            minItems: items.length,
            maxItems: items.length,
            uniqueItems: true,
        };
    } else {
        return {
            type: 'array',
            maxItems: 0,
        };
    }
}

function getPatternForListedString(items: string[]) {
    let reg = '';

    if (items instanceof Array) {
        items.forEach((item) => {
            reg += `(?=.*${item})`;
        });
    }

    return reg;
}

function getListedStringSchema(items: string[]): SchemaPattern {
    if (items?.length > 0) {
        return {
            type: 'string',
            pattern: getPatternForListedString(items),
            minLength: items ? items?.join(',')?.length : 0,
            maxLength: items ? items?.join(',')?.length : 0,
        };
    } else {
        return {
            type: 'null',
        };
    }
}

function getIncludeGuestsOrExternalUsers(
    includeGuestsOrExternalUsers: string[],
    isIncluded: boolean,
    includeUsersConfig: string,
) {
    if (includeUsersConfig === 'All' || includeUsersConfig === 'None') {
        return {
            type: 'null',
        };
    } else if (includeGuestsOrExternalUsers?.length > 0 && isIncluded) {
        return {
            type: 'object',

            properties: {
                guestOrExternalUserTypes: {
                    type: 'string',
                    pattern: getPatternForListedString(includeGuestsOrExternalUsers),
                    minLength: includeGuestsOrExternalUsers ? includeGuestsOrExternalUsers?.join(',')?.length : 0,
                    maxLength: includeGuestsOrExternalUsers ? includeGuestsOrExternalUsers?.join(',')?.length : 0,
                },
            },

            required: ['guestOrExternalUserTypes'],
        };
    } else {
        return {
            type: 'null',
        };
    }
}

export function NoCapSchema(displayName: string) {
    return {
        type: 'array',
        not: {
            contains: {
                type: 'object',
                properties: {
                    displayName: {
                        type: 'string',
                        const: displayName,
                    },
                },
            },
        },
    };
}

function getRequiredConditions(conditions: FormConditions): string[] | undefined {
    const required = [];
    if (conditions.insiderRiskLevels?.length > 0) {
        required.push('insiderRiskLevels');
    }

    if (conditions?.authenticationFlows?.transferMethods?.length > 0) {
        required.push('authenticationFlows');
    }

    if (required.length > 0) {
        return required;
    }

    return;
}
